import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Group, Stack, Text, ThemeIcon, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconCircleCheck } from '@tabler/icons-react';
import { handleApiError } from 'api/error-handling';
import {
  buildInitialLevels,
  getDefaultLevel,
  sectionHasLevels,
  sectionHasServerResult,
} from 'components/Workout/logging/levelUtils';
import {
  EnrichedWorkout,
  MovementResult,
  ResultsState,
  isMovementResultFilled,
} from 'components/Workout/logging/types';
import {
  buildInitialResults,
  buildResultPayload,
  enrichSections,
} from 'components/Workout/logging/workoutLoggerUtils';
import useClassWorkoutDetails from 'hooks/workout/useClassWorkoutDetails';
import useMyWorkoutResult from 'hooks/workoutAthleteResult/useMyWorkoutResult';
import useSaveWorkoutAthleteResult from 'hooks/workoutAthleteResult/useSaveWorkoutAthleteResult';
import useLogWorkoutResult from 'hooks/workoutResult/useLogWorkoutResult';
import useSetSectionLevel from 'hooks/workoutResult/useSetSectionLevel';
import { WorkoutLevelEnum } from 'types/enums';
import notificationHandler from 'utils/notifications';

import AthleteLegacyClassNotes from './AthleteLegacyClassNotes';
import AthleteWorkoutSectionDetail from './AthleteWorkoutSectionDetail';
import LogResultsPanel from './LogResults/LogResultsPanel';
import WorkoutLoggerSkeleton from './WorkoutLoggerSkeleton';

interface WorkoutLoggerProps {
  kiloClassId: string;
  athleteKiloClassId: string | undefined;
  hasStarted: boolean;
  legacyNotes?: string | null;
}

const WorkoutLogger = ({
  kiloClassId,
  athleteKiloClassId,
  hasStarted,
  legacyNotes,
}: WorkoutLoggerProps): JSX.Element | null => {
  const theme = useMantineTheme();
  const [isOpened, { toggle }] = useDisclosure(false);
  const { data: workoutData, isLoading } = useClassWorkoutDetails({ kiloClassId });
  const workoutId = workoutData?.data?.workoutId;
  // notificationHandler() returns fresh closures every call; memoize so downstream
  // useCallback deps don't churn on every render.
  const { errorNotification, successNotification } = useMemo(() => notificationHandler(), []);
  const [notes, setNotes] = useState('');
  const notesInitialized = useRef(false);

  // workoutId is sufficient: workout structure (sections/movements/scoring) is
  // immutable for an in-progress class, so ignore array identity changes on refetch.
  const enrichedSections = useMemo(
    () => (workoutData?.data ? enrichSections(workoutData.data.sections) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workoutId],
  );

  const athleteResultQuery = useMyWorkoutResult(workoutId);
  // Destructure the stable mutateAsync (memoized inside react-query v3) so the
  // useCallback below doesn't rebuild on each mutation lifecycle transition.
  const { mutateAsync: logResultMutation } = useLogWorkoutResult(workoutId);
  const { mutateAsync: saveNotesMutation } = useSaveWorkoutAthleteResult(workoutId);
  const { mutateAsync: setSectionLevelMutation } = useSetSectionLevel(workoutId);

  const [results, setResults] = useState<ResultsState>({});
  // Snapshot of the server's last-known view per movement, used to detect
  // local edits and preserve them across refetches (initial hydration, save
  // invalidation, and 60-s polling for coach-side edits).
  const lastServerSnapshotRef = useRef<ResultsState>({});

  useEffect(() => {
    if (enrichedSections.length === 0 || athleteResultQuery.isLoading) return;
    const apiResults = athleteResultQuery.data?.data?.results ?? [];
    const newServerState = buildInitialResults(enrichedSections, apiResults);
    const prevServerSnapshot = lastServerSnapshotRef.current;

    setResults(prev => {
      const merged: ResultsState = {};
      for (const id of Object.keys(newServerState)) {
        const prevLocal = prev[id];
        const prevServer = prevServerSnapshot[id];
        const hasLocalEdit =
          prevLocal !== undefined &&
          prevServer !== undefined &&
          JSON.stringify(prevLocal) !== JSON.stringify(prevServer);
        merged[id] = hasLocalEdit ? prevLocal : newServerState[id];
      }
      return merged;
    });

    lastServerSnapshotRef.current = newServerState;
    // athleteResultQuery.isLoading is read inside but excluded from deps on purpose:
    // the early return handles the transient state, and listing it would re-run
    // the merge needlessly when loading flips without a new payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutId, athleteResultQuery.data, enrichedSections]);

  const [sectionLevels, setSectionLevels] = useState<Record<string, WorkoutLevelEnum | null>>({});
  const lastServerLevelSnapshotRef = useRef<Record<string, WorkoutLevelEnum | null>>({});

  useEffect(() => {
    if (enrichedSections.length === 0 || athleteResultQuery.isLoading) return;
    const apiResults = athleteResultQuery.data?.data?.results ?? [];
    const newServerState = buildInitialLevels(enrichedSections, apiResults);
    const prevServerSnapshot = lastServerLevelSnapshotRef.current;

    setSectionLevels(prev => {
      const merged: Record<string, WorkoutLevelEnum | null> = {};
      for (const id of Object.keys(newServerState)) {
        const prevLocal = prev[id];
        const prevServer = prevServerSnapshot[id];
        const hasLocalEdit = prevLocal !== undefined && prevServer !== undefined && prevLocal !== prevServer;
        merged[id] = hasLocalEdit ? prevLocal : newServerState[id];
      }
      return merged;
    });

    lastServerLevelSnapshotRef.current = newServerState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutId, athleteResultQuery.data, enrichedSections]);

  useEffect(() => {
    if (notesInitialized.current) return;
    const savedNotes = athleteResultQuery.data?.data?.notes;
    if (savedNotes) {
      notesInitialized.current = true;
      setNotes(savedNotes);
    }
  }, [athleteResultQuery.data]);

  const handleNotesChange = useCallback((value: string): void => {
    notesInitialized.current = true;
    setNotes(value);
  }, []);

  const handleResultChange = useCallback((movementId: string, result: MovementResult): void => {
    setResults(prev => ({ ...prev, [movementId]: result }));
  }, []);

  const handleResultCommit = useCallback(
    async (movementId: string, result: MovementResult): Promise<void> => {
      // Throw on empty result so the caller's catch keeps the indicator dirty
      // instead of misleadingly transitioning saving → saved with nothing sent.
      if (!isMovementResultFilled(result)) throw new Error('Result is empty; not committing.');
      const section = enrichedSections.find(s =>
        s.movements.some(m => m.workoutSectionMovementId === movementId),
      );
      const level =
        section && sectionHasLevels(section)
          ? sectionLevels[section.workoutSectionId] ?? getDefaultLevel(section)
          : null;
      try {
        await logResultMutation({ ...buildResultPayload(movementId, result, athleteKiloClassId), level });
      } catch (err) {
        // Backend messages are the fallback when frontend guidance fails — surface
        // the server's `message` field so the athlete sees an actionable reason.
        errorNotification('Failed to save result', handleApiError(err).message);
        throw err;
      }
    },
    [logResultMutation, athleteKiloClassId, errorNotification, enrichedSections, sectionLevels],
  );

  const handleLevelChange = useCallback((sectionId: string, level: WorkoutLevelEnum): void => {
    setSectionLevels(prev => ({ ...prev, [sectionId]: level }));
  }, []);

  const handleLevelCommit = useCallback(
    async (sectionId: string, level: WorkoutLevelEnum): Promise<void> => {
      const section = enrichedSections.find(s => s.workoutSectionId === sectionId);
      const apiResults = athleteResultQuery.data?.data?.results ?? [];
      // No result logged yet for this section — nothing to bulk-update server-side;
      // the picked level rides along on the section's first commit instead.
      if (!section || !sectionHasServerResult(section, apiResults)) return;
      try {
        await setSectionLevelMutation({ workoutSection: sectionId, level });
      } catch (err) {
        errorNotification('Failed to save level', handleApiError(err).message);
        throw err;
      }
    },
    [enrichedSections, athleteResultQuery.data, setSectionLevelMutation, errorNotification],
  );

  const handleNotesCommit = useCallback(
    async (value: string): Promise<void> => {
      if (!workoutId) return;
      try {
        await saveNotesMutation({ workout: workoutId, notes: value });
        successNotification('Notes saved');
      } catch (err) {
        errorNotification('Failed to save notes', handleApiError(err).message);
      }
    },
    [saveNotesMutation, workoutId, successNotification, errorNotification],
  );

  if (isLoading) return <WorkoutLoggerSkeleton />;

  const workout = workoutData?.data;
  if (!workout) return <AthleteLegacyClassNotes notes={legacyNotes} />;

  const canLog = hasStarted && !!athleteKiloClassId;

  if (canLog && athleteResultQuery.isLoading) return <WorkoutLoggerSkeleton />;

  const enrichedWorkout: EnrichedWorkout = { ...workout, sections: enrichedSections };

  return (
    <Stack spacing='xs'>
      <Text color='dimmed' fs='italic'>
        Workout Program
      </Text>

      {!canLog ? (
        <>
          <Box
            py='md'
            px='lg'
            onClick={toggle}
            sx={{
              cursor: 'pointer',
              borderRadius: theme.radius.md,
              border: `1px solid ${theme.colors.green[7]}`,
            }}
          >
            <Group position='apart' noWrap>
              <Group spacing='sm' noWrap>
                <ThemeIcon color='green' variant='light' radius='xl' size='md'>
                  <IconCircleCheck size={20} />
                </ThemeIcon>
                <Text color='gray.8' fw={600} lineClamp={1}>
                  {workout.name}
                </Text>
              </Group>
              <IconChevronDown
                color={theme.colors.dark[3]}
                size={theme.fontSizes.lg}
                style={{
                  transform: isOpened ? 'rotate(180deg)' : 'unset',
                  transition: 'ease-in 100ms',
                  flexShrink: 0,
                }}
              />
            </Group>
          </Box>
          <AthleteWorkoutSectionDetail workout={workout} isOpened={isOpened} />
        </>
      ) : (
        <LogResultsPanel
          workout={enrichedWorkout}
          kiloClassId={kiloClassId}
          results={results}
          sectionLevels={sectionLevels}
          notes={notes}
          onResultChange={handleResultChange}
          onResultCommit={handleResultCommit}
          onLevelChange={handleLevelChange}
          onLevelCommit={handleLevelCommit}
          onNotesChange={handleNotesChange}
          onNotesCommit={handleNotesCommit}
        />
      )}
    </Stack>
  );
};

export default WorkoutLogger;
